import React, { useEffect, useRef, useState, useCallback, ComponentProps, MouseEventHandler, } from 'react';
import { Feature, MultiPolygon, Polygon } from 'geojson';
import {
  geojsonToWKT,
  wktToGeoJSON
} from '@terraformer/wkt';
import { useForm, UseFormReturn, useWatch } from "react-hook-form"
import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon"
import Color from "color"

type PolygonEditorValues = {
  // NORMALISED polygons (values between 0 and 1)
  polygons: Array<Feature<Polygon>>;
}

interface PolygonEditorHook<TElement extends HTMLElement> {
  /** Attach to element we should overlay */
  targetRef: React.MutableRefObject<TElement | null>;
  /** Attach to canvas where polyogns are drawn */
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>
  canvasProps: ComponentProps<"canvas">

  form: UseFormReturn<PolygonEditorValues>

  /** Redraw and recompute according to target ref and polygon values  */
  reload: () => void

  util: {
    /** Converts a list of polygon features to a MultiPolygon WKT */
    toWkt: (polygons: Array<Feature<Polygon>>) => string;
    /** Converts a MultiPolygon WKT to a list of polygon features */
    fromWkt: (wkt: string) => Array<Feature<Polygon>>;
  }

}

const POLYGON_CLOSE_PROXIMITY_PX = 20

/**
 * Hook which handles the drawing, transforming etc. of closed polygons.
 * 
 * Uses GeoJSON as the storage layer for our polygons, and provides utilities for conversion
 * to/from WKT also.
*/
export function usePolygonEditor<TElement extends HTMLElement = HTMLDivElement>(args?: {
  strokeColor?: string;
  selectedStrokeColor?: string
  fillColor?: string;
  selectedFillColor?: string
  readonly?: boolean
}): PolygonEditorHook<TElement> {
  const {
    fillColor = new Color("#BCD752").alpha(0.3).rgb().toString(), // Lime green
    strokeColor = "#BCD752", // Lime green
    selectedFillColor = new Color("#FFA500").alpha(0.3).rgb().toString(), // Orange
    selectedStrokeColor = "#FFA500", // Orange
    readonly = false
  } = args ?? {}

  /* Refs */
  const targetRef = useRef<TElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** Currently drawn polygon. When closed, it will be added to the form state */
  const [tempPolygon, setTempPolygon] = useState<Feature<Polygon> | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const form = useForm<PolygonEditorValues>({
    defaultValues: {
      polygons: []
    }
  });
  const [selectedPolyIdx, setSelectedPolyIdx] = useState<number>(-1);
  const [dragging, setDragging] = useState<{ polyIdx: number; pointIdx: number } | null>(null);
  const [canvasStyle, setCanvasStyle] = useState<ComponentProps<'canvas'>["style"]>({
    position: 'absolute',
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    pointerEvents: 'auto',
    zIndex: 10,
    visibility: "hidden"
  });
  const polygons = useWatch({
    control: form.control,
    name: "polygons"
  })

  // Convert canvas coordinates to normalized coordinates (0-1)
  const canvasToNormalized = useCallback((x: number, y: number): [number, number] => {
    if (!canvasRef.current) return [0, 0];
    const canvas = canvasRef.current;
    return [x / canvas.width, y / canvas.height];
  }, []);

  // Convert normalized coordinates (0-1) to canvas coordinates
  const normalizedToCanvas = useCallback((x: number, y: number): [number, number] => {
    if (!canvasRef.current) return [0, 0];
    const canvas = canvasRef.current;
    return [x * canvas.width, y * canvas.height];
  }, []);

  const drawPolygons = useCallback(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw completed polygons
    polygons.forEach((polygon, i) => {
      if (polygon.geometry.type !== "Polygon") return;

      if (polygon.geometry.coordinates.length === 0) return;
      const isSelected = selectedPolyIdx === i

      const coordinates = polygon.geometry.coordinates[0]; // First ring
      if (coordinates.length < 3) return;

      ctx.beginPath();
      ctx.strokeStyle = isSelected ? selectedStrokeColor : strokeColor;
      ctx.fillStyle = isSelected ? selectedFillColor : fillColor;
      ctx.lineWidth = 2;

      // Draw polygon
      coordinates.forEach((coord, i) => {
        const [x, y] = normalizedToCanvas(coord[0], coord[1]);
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Draw vertices
      coordinates.forEach((coord) => {
        const [x, y] = normalizedToCanvas(coord[0], coord[1]);

        if (isSelected) {
          ctx.beginPath();
          ctx.arc(x, y, 7, 0, 2 * Math.PI);
          ctx.fillStyle = selectedStrokeColor
          ctx.fill();
          ctx.beginPath();
          ctx.arc(x, y, 5, 0, 2 * Math.PI);
          ctx.fillStyle = "white";
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(x, y, 3, 0, 2 * Math.PI);
          ctx.fillStyle = strokeColor;
          ctx.fill();
        }
      });
    });

    // Draw temporary polygon
    if (tempPolygon && tempPolygon.geometry.coordinates[0].length > 0) {
      const coordinates = tempPolygon.geometry.coordinates[0];
      const hasEnoughPoints = coordinates.length >= 3;
      let shouldShowClosePreview = false;

      // Check if we should show close preview (mouse is near first point)
      if (mousePosition && hasEnoughPoints) {
        const [firstX, firstY] = normalizedToCanvas(coordinates[0][0], coordinates[0][1]);
        const distance = Math.sqrt(
          Math.pow(mousePosition.x - firstX, 2) +
          Math.pow(mousePosition.y - firstY, 2)
        );
        shouldShowClosePreview = distance < POLYGON_CLOSE_PROXIMITY_PX;
      }

      // Draw the preview of the closed polygon if we're close to the start
      if (shouldShowClosePreview) {
        ctx.beginPath();
        ctx.strokeStyle = strokeColor;
        ctx.fillStyle = fillColor;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);

        // Draw the complete polygon
        coordinates.forEach((coord, i) => {
          const [x, y] = normalizedToCanvas(coord[0], coord[1]);
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });

        // Close the polygon
        const [firstX, firstY] = normalizedToCanvas(coordinates[0][0], coordinates[0][1]);
        ctx.lineTo(firstX, firstY);

        ctx.stroke();
        ctx.setLineDash([]);

        // Draw a highlight circle around the first point
        ctx.beginPath();
        ctx.arc(firstX, firstY, POLYGON_CLOSE_PROXIMITY_PX / 2, 0, 2 * Math.PI);
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 1;
        ctx.fillStyle = `${strokeColor}20`; // 20% opacity
        ctx.fill();
        ctx.stroke();
      }

      // Draw the normal temporary polygon
      ctx.beginPath();
      ctx.strokeStyle = strokeColor;
      ctx.fillStyle = fillColor;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]); // Dashed line

      coordinates.forEach((coord, i) => {
        const [x, y] = normalizedToCanvas(coord[0], coord[1]);
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      // Draw line to mouse position if we have points
      if (mousePosition && coordinates.length > 0) {
        // If we're showing close preview, draw to the first point instead of mouse
        if (shouldShowClosePreview) {
          const [firstX, firstY] = normalizedToCanvas(coordinates[0][0], coordinates[0][1]);
          ctx.lineTo(firstX, firstY);
        } else {
          ctx.lineTo(mousePosition.x, mousePosition.y);
        }
      }

      ctx.stroke();
      ctx.setLineDash([]); // Reset dash

      // Draw vertices for temp polygon
      coordinates.forEach((coord) => {
        const [x, y] = normalizedToCanvas(coord[0], coord[1]);
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, 2 * Math.PI);
        ctx.fillStyle = strokeColor;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, 2 * Math.PI);
        ctx.fillStyle = "white";
        ctx.lineWidth = 2;
        ctx.fill();
      });
    }
  }, [polygons, tempPolygon, mousePosition, normalizedToCanvas, selectedPolyIdx, strokeColor, selectedFillColor, selectedStrokeColor, fillColor]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const target = targetRef.current;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    setCanvasStyle(s => ({
      ...s,
      width: rect.width,
      height: rect.height,
      visibility: "visible"
    }));
    canvas.width = rect.width;
    canvas.height = rect.height;

    drawPolygons();
  }, [drawPolygons, canvasRef, targetRef, setCanvasStyle]);

  const handleCanvasMouseDown: MouseEventHandler<HTMLCanvasElement> = useCallback((event) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const [normalizedX, normalizedY] = canvasToNormalized(x, y);


    if (!tempPolygon) {
      // First check for dragging existing vertex
      const existing = form.getValues('polygons') || [];
      for (let i = 0; i < existing.length; i++) {
        const coords = existing[i].geometry.coordinates[0];
        for (let j = 0; j < coords.length; j++) {
          const [cx, cy] = normalizedToCanvas(coords[j][0], coords[j][1]);
          const dist = Math.hypot(x - cx, y - cy);
          if (dist < POLYGON_CLOSE_PROXIMITY_PX / 2) {
            setSelectedPolyIdx(i);
            setDragging({ polyIdx: i, pointIdx: j });
            return;
          }
        }
      }

      // Check if clicking inside existing polygon
      const polygons = form.getValues("polygons")
      const insidePolygon = polygons.findIndex(poly => {
        return booleanPointInPolygon([normalizedX, normalizedY], poly)
      })

      if (insidePolygon >= 0) {
        setSelectedPolyIdx(insidePolygon)
        return
      } else {
        setSelectedPolyIdx(-1)
      }

      // Start new polygon
      const newPolygon: Feature<Polygon> = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[[normalizedX, normalizedY]]]
        },
        properties: {}
      };
      setTempPolygon(newPolygon);
      return
    }

    // Add point to existing polygon
    const coordinates = [...tempPolygon.geometry.coordinates[0]];

    // Check if clicking near the first point to close polygon
    if (coordinates.length >= 3) {
      const [firstX, firstY] = normalizedToCanvas(coordinates[0][0], coordinates[0][1]);
      const distance = Math.sqrt((x - firstX) ** 2 + (y - firstY) ** 2);

      if (distance < POLYGON_CLOSE_PROXIMITY_PX) { // Close polygon if within 10 pixels of start
        // Close the polygon and add to form
        const closedCoordinates = [...coordinates, coordinates[0]]; // Close the ring
        const completedPolygon: Feature<Polygon> = {
          ...tempPolygon,
          geometry: {
            ...tempPolygon.geometry,
            coordinates: [closedCoordinates]
          }
        };

        const currentPolygons = form.getValues('polygons') || [];
        form.setValue('polygons', [...currentPolygons, completedPolygon], { shouldDirty: true });
        setTempPolygon(null);
        return;
      }
    }

    // Add new point
    coordinates.push([normalizedX, normalizedY]);
    setTempPolygon({
      ...tempPolygon,
      geometry: {
        ...tempPolygon.geometry,
        coordinates: [coordinates]
      }
    });
  }, [tempPolygon, canvasToNormalized, normalizedToCanvas, form, canvasRef]);

  const handleMouseMove: MouseEventHandler<HTMLCanvasElement> = useCallback(
    (event) => {
      if (!canvasRef.current) return;

      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      setMousePosition({ x, y });

      if (dragging) {
        const [nx, ny] = canvasToNormalized(x, y);
        const polys = form.getValues('polygons') || [];
        const updated = polys.map((p, i) => {
          if (i !== dragging.polyIdx) return p;
          const coords = [...p.geometry.coordinates[0]];
          coords[dragging.pointIdx] = [nx, ny];
          if (dragging.pointIdx === 0) {
            coords[coords.length - 1] = [nx, ny];
          } else if (dragging.pointIdx === coords.length - 1) {
            coords[0] = [nx, ny];
          }
          return {
            ...p,
            geometry: { ...p.geometry, coordinates: [coords] },
          };
        });
        form.setValue('polygons', updated, { shouldDirty: true });
      }
    },
    [canvasRef, dragging, canvasToNormalized, form]
  );

  const undoLastPoint = useCallback(() => {
    if (tempPolygon && tempPolygon.geometry.coordinates[0].length > 0) {
      // Remove last point
      const coordinates = [...tempPolygon.geometry.coordinates[0]];
      coordinates.pop();

      if (coordinates.length === 0) {
        setTempPolygon(null);
      } else {
        setTempPolygon({
          ...tempPolygon,
          geometry: {
            ...tempPolygon.geometry,
            coordinates: [coordinates]
          }
        });
      }
    }
  }, [tempPolygon]);

  const deleteSelectedPolygon = useCallback(() => {
    if (selectedPolyIdx >= 0) {
      const polygons = form.getValues('polygons') || [];
      form.setValue('polygons', polygons.filter((_, idx) => idx !== selectedPolyIdx), { shouldDirty: true });
      setSelectedPolyIdx(-1);
    }
  }, [selectedPolyIdx, form]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if ((event.key === 'Backspace' || event.key === 'Delete') && (selectedPolyIdx >= 0)) {
      deleteSelectedPolygon();
      return;
    }

    if (event.key === 'Escape') {
      setTempPolygon(null);
      setSelectedPolyIdx(-1);
      return;
    }

    if ((event.key === 'Backspace' || event.key === 'Delete') && selectedPolyIdx >= 0) {
      deleteSelectedPolygon()
      return
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') { 
      undoLastPoint();
      return;
    }
  }, [deleteSelectedPolygon, selectedPolyIdx, setSelectedPolyIdx, undoLastPoint]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener("keydown", handleKeyDown);
      // Make canvas focusable so it can receive key events
      canvas.tabIndex = 0;
      canvas.focus();

      return () => {
        canvas.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [handleKeyDown])

  const reload = useCallback(() => {
    resizeCanvas();
    drawPolygons();
  }, [resizeCanvas, drawPolygons]);

  // Redraw when polygons change
  useEffect(() => {
    drawPolygons();
  }, [drawPolygons, tempPolygon, mousePosition]);

  // Set up resize observer for target element
  useEffect(() => {
    if (!targetRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      resizeCanvas();
    });

    resizeObserver.observe(targetRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [resizeCanvas]);

  useEffect(() => {
    const listener: EventListener = (e: Event) => {
      // If click target is outside canvas:
      // - Deselect selected polygon
      // - Cancel temp polygon
      if (!canvasRef.current?.contains(e.target as Node)) {
        setSelectedPolyIdx(-1)
        setTempPolygon(null)
      }
    }

    document.addEventListener("click", listener)

    return () => document.removeEventListener("click", listener)
  }, [])

  const canvasProps: ComponentProps<"canvas"> = {
    style: canvasStyle,
    ...(
      readonly ? {} : {
        onMouseDown: handleCanvasMouseDown,
        onMouseMove: handleMouseMove,
        onMouseUp: () => setDragging(null)
      }
    )
  }

  return {
    targetRef,
    canvasRef,
    reload,
    canvasProps,
    util: {
      toWkt,
      fromWkt
    },
    form
  };
}


const fromWkt = (wkt: string): Array<Feature<Polygon>> => {
  if (!wkt.trim()) {
    return [];
  }

  try {
    const geojson = wktToGeoJSON(wkt);

    if (geojson.type !== "MultiPolygon") {
      return []
    }

    return geojson.coordinates.map((c) => {
      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: c
        },
        properties: {}
      }
    })
  } catch (error) {
    console.error('Error parsing WKT:', error);
    return [];
  }
}

const toWkt = (polygons: Array<Feature<Polygon>>): string => {

  if(polygons.length === 0) {
    return ''
  }
  // Map to Multipolygon type with [x,y]
  const multiPolygon: MultiPolygon = {
    type: 'MultiPolygon',
    coordinates: polygons.map((p) => p.geometry.coordinates)
  };

  try {
    return geojsonToWKT(multiPolygon);
  } catch (error) {
    console.error('Error converting to WKT:', error);
    return '';
  }
}